/**
 * The chime that says somebody has turned up on a chat widget.
 *
 * Synthesised rather than played from a file. A two-note chime is about forty
 * lines of oscillator here against a binary in the repo, a request on every load,
 * and one more thing that can 404 behind a CDN — and the portal has no audio
 * pipeline of any kind to hang an asset off.
 *
 * The hard part is not the sound, it is being allowed to make it. Browsers start
 * an `AudioContext` suspended until the page has seen a real user gesture, and a
 * `resume()` outside one is rejected. So the context is created once, primed from
 * the first click or keypress anywhere in the portal (`primeAudio`, called by the
 * layout), and every failure along the way is swallowed: an alert that cannot be
 * heard is a smaller problem than an alert that throws.
 */

type WindowWithLegacyAudio = Window &
  typeof globalThis & { webkitAudioContext?: typeof AudioContext };

let context: AudioContext | null = null;
/** Set once we know the environment has no Web Audio at all — stop retrying. */
let unavailable = false;

function audioContext(): AudioContext | null {
  if (unavailable) return null;
  if (context) return context;

  const Ctor =
    typeof window === 'undefined'
      ? undefined
      : window.AudioContext ?? (window as WindowWithLegacyAudio).webkitAudioContext;
  if (!Ctor) {
    unavailable = true;
    return null;
  }

  try {
    context = new Ctor();
    return context;
  } catch {
    unavailable = true;
    return null;
  }
}

/**
 * Let the page make sound, if it is allowed to yet.
 *
 * Safe to call as often as you like: resuming a running context is a no-op, and
 * a rejection just means the gesture requirement has not been met and the next
 * call can try again.
 */
export function primeAudio(): void {
  const ctx = audioContext();
  if (!ctx || ctx.state !== 'suspended') return;
  void ctx.resume().catch(() => {
    // Not from a user gesture. The next click will do it.
  });
}

/** One note. Sine, because anything richer reads as an alarm rather than a nudge. */
function note(ctx: AudioContext, hertz: number, startsAt: number, seconds: number): void {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.value = hertz;

  // A short attack and an exponential tail. A raw on/off would click audibly at
  // both ends, and `exponentialRampToValueAtTime` cannot reach zero — hence the
  // small floor before the stop.
  gain.gain.setValueAtTime(0.0001, startsAt);
  gain.gain.exponentialRampToValueAtTime(0.12, startsAt + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + seconds);

  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start(startsAt);
  oscillator.stop(startsAt + seconds + 0.02);
}

/**
 * Two rising notes, about a third of a second in total.
 *
 * Quiet and short on purpose. This fires while somebody is working in another
 * tab, so it has to be recognisable without being the loudest thing that has
 * happened to them all afternoon.
 */
export function playVisitorChime(): void {
  const ctx = audioContext();
  if (!ctx) return;

  // Still blocked: ask again — the gesture may have happened since — and give up
  // quietly for this alert rather than queueing a sound to arrive out of context.
  if (ctx.state === 'suspended') {
    primeAudio();
    if (ctx.state === 'suspended') return;
  }

  try {
    const now = ctx.currentTime;
    note(ctx, 587.33, now, 0.16);        // D5
    note(ctx, 880.0, now + 0.11, 0.22);  // A5
  } catch {
    // A context the browser has torn down (backgrounded too long on mobile).
    // Nothing to recover: the toast still carries the message.
  }
}

/**
 * A louder note. `playVisitorChime`'s 0.12 peak is tuned to be a nudge, not an
 * interruption — right for "somebody is on a widget" but wrong for "somebody is
 * waiting on a person", which is the one alert here with a clock on it.
 */
function urgentNote(ctx: AudioContext, hertz: number, startsAt: number, seconds: number): void {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.value = hertz;

  gain.gain.setValueAtTime(0.0001, startsAt);
  gain.gain.exponentialRampToValueAtTime(0.4, startsAt + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + seconds);

  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start(startsAt);
  oscillator.stop(startsAt + seconds + 0.02);
}

/**
 * Three notes, louder and lower than the visitor chime, for the one alert that
 * means somebody is actually waiting on a person rather than just present on a
 * widget. Distinct on purpose — this is the alert an admin should be able to
 * tell apart without looking at the screen.
 */
export function playHandoffChime(): void {
  const ctx = audioContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    primeAudio();
    if (ctx.state === 'suspended') return;
  }

  try {
    const now = ctx.currentTime;
    urgentNote(ctx, 660.0, now, 0.15);         // E5
    urgentNote(ctx, 660.0, now + 0.19, 0.15);  // E5 again — repetition reads as urgent
    urgentNote(ctx, 880.0, now + 0.38, 0.28);  // A5
  } catch {
    // Same fallback as playVisitorChime: the toast still carries the message.
  }
}
